import { DeleteOutlined, LoadingOutlined } from '@ant-design/icons';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    List,
    ListItem,
    ListItemText,
} from '@mui/material';
import CityDto from 'common/dto-types/city-dto';
import { sortTournamentsByStartDateDesc } from 'common/dto-types/tournament-dto';
import IconButton from 'components/@extended/IconButton';
import { useState } from 'react';
import { dispatch } from 'store';
import { openSnackbar } from 'store/reducers/snackbar';
import { deleteCity } from './api';

type CityDeleteButtonProps = {
    city: CityDto;
    refreshCallback: () => Promise<void>;
};

const CityDeleteButton = ({ city, refreshCallback }: CityDeleteButtonProps): JSX.Element => {
    const [isLoading, setIsLoading] = useState(false);
    const [isOpen, setIsOpen] = useState(false);

    const tournaments = sortTournamentsByStartDateDesc(city.tournaments).filter((tournament) =>
        Boolean(tournament.name),
    );

    const handleClose = () => {
        setIsOpen(false);
    };

    const handleDelete = async () => {
        setIsLoading(true);

        const response = await deleteCity(city.id!);

        if (response?.status === 200) {
            dispatch(
                openSnackbar({
                    open: true,
                    message: 'Host City deleted successfully!',
                    variant: 'alert',
                    alert: {
                        color: 'success',
                    },
                }),
            );
            await refreshCallback();
        } else {
            const references = response?.data?.references;
            const referenceDetail = references
                ? ` (Venues: ${references.venues}, Events: ${references.events}, Contacts: ${references.contacts})`
                : '';
            dispatch(
                openSnackbar({
                    open: true,
                    message: `${response?.data?.message ?? 'Cannot delete Host City!'}${referenceDetail}`,
                    variant: 'alert',
                    alert: {
                        color: 'error',
                    },
                }),
            );
        }

        setIsLoading(false);
        setIsOpen(false);
    };

    if (isLoading) return <LoadingOutlined style={{ padding: '9px' }} />;

    return (
        <>
            <IconButton onClick={() => setIsOpen(true)} shape="rounded" color="error">
                <DeleteOutlined />
            </IconButton>
            <Dialog open={isOpen} onClose={handleClose} aria-labelledby="responsive-dialog-title">
                <DialogTitle id="responsive-dialog-title">Are you sure you want to delete this Host City?</DialogTitle>
                <DialogContent>
                    {tournaments.length > 0 ? (
                        <>
                            <DialogContentText>This city is being used in these Tournaments:</DialogContentText>
                            <List dense>
                                {tournaments.map((tournament) => (
                                    <ListItem key={tournament.fdcpId ?? tournament.name} sx={{ py: 0 }}>
                                        <ListItemText primary={tournament.name} />
                                    </ListItem>
                                ))}
                            </List>
                            <DialogContentText>Are you sure you want to delete it?</DialogContentText>
                        </>
                    ) : (
                        <DialogContentText>Deleting is irreversible! Proceed?</DialogContentText>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button autoFocus onClick={handleClose}>
                        Cancel
                    </Button>
                    <Button onClick={handleDelete} autoFocus color="error" disabled={isLoading}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default CityDeleteButton;
